import {Injector, ViewContainerRef} from '@angular/core';
import * as _ from 'lodash-es';
import {NEVER, Observable, Subject, Subscription} from 'rxjs';
import {DragHandler, DragHandlerAxis, PointerPositionInfo, X} from '../../../../../common/drag-handler';
import {dependOn} from '../../../../../common/rxjs/depend-on';
import {DynamicComponentFactoryService, DynamicComponentRef} from '../../../../dynamic-component';
import {TableColumnConfig} from '../../table';
import {
    CommonTablePluginMessageType,
    TablePluginMsgColumnsConfig,
    TablePluginMsgColumnsConfigPreview
} from '../common-table-plugin-messages';
import {ResizingTablePluginMessageType} from '../table-columns-resizer';
import {
    TablePlugin,
    TablePluginColumnCellRenderedArgs,
    TablePluginColumnsSetupArgs,
    TablePluginInitTablePluginArgs,
    TablePluginMessage
} from '../table-plugin';
import {TableColumnMovingGhostComponent} from './table-column-moving-ghost.component';
import {TableColumnReorderColumnMeta} from './table-column-reorder-column-meta';
import {TableColumnReorderColumnsArea} from './table-column-reorder-columns-area';

interface ColumnMovingGhostMeta {
    shiftX: number;
    lastPreviewShiftX: number;
    width: number;
    initialColumnsAreaProjectionLeft: number;
    columnsAreaProjectionLeft: number;
    ghostColumnsArea: TableColumnReorderColumnsArea;
    columnsAreas: TableColumnReorderColumnsArea[];
    initialColumnsAreaIndex: number;
    newColumnsAreaIndex: number;
    lastPreviewColumnsAreaIndex: number;
    lastColumnsConfigPreview: TableColumnConfig[];
    overlappedColumnsAreas: TableColumnReorderColumnsArea[];
    lastAreaToSwapWith: TableColumnReorderColumnsArea;
    isLastMoveDirectionLeft: boolean;
    ghostComponentRef: DynamicComponentRef<TableColumnMovingGhostComponent>;
}

const TABLE_COLUMN_GHOST_OVERLAPPING_CLASS = 'table-column-reorder-ghost-overlap';

const COLUMN_MOVING_INITIAL_THRESHOLD_PX = 10;
const COLUMN_OVERLAPPING_THRESHOLD_FRACTION = 0.5;

export class TableColumnReorderPlugin implements TablePlugin {

    private static moveElementInArrayByIndex<T>(arr: T[], fromIndex: number, toIndex: number): T[] {
        const item: T = arr.splice(fromIndex, 1)[0];

        arr.splice(toIndex, 0, item);

        return arr;
    }

    private isColumnsInitialized: boolean = false;
    private columns: TableColumnReorderColumnMeta[] = [];
    private destroy$: Observable<void>;
    private message$: Subject<TablePluginMessage>;
    private pointerEventsSubscription: Subscription;
    private afterTableViewContainerRef: ViewContainerRef;
    private columnMovingGhostMeta: ColumnMovingGhostMeta;

    private readonly dynamicComponentFactoryService = this.injector.get(DynamicComponentFactoryService);
    private readonly dragHandler = new DragHandler(this.injector);

    constructor(
        private injector: Injector
    ) {}

    public initTablePlugin(args: TablePluginInitTablePluginArgs): void {
        this.destroy$ = args.destroy$;
        this.message$ = args.message$;
        this.afterTableViewContainerRef = args.afterTableViewContainerRef;
    }

    public columnsSetup(args: TablePluginColumnsSetupArgs): void {
        if (!this.columnMovingGhostMeta) {
            if (this.pointerEventsSubscription) {
                this.pointerEventsSubscription.unsubscribe();
            }

            this.isColumnsInitialized = false;
            this.columns = args.columnsConfig.map(columnConfig =>
                this.columns.find(columnMeta => columnMeta.columnConfig.key === columnConfig.key)
                || {
                    columnDirective: null,
                    viewRef: null,
                    element: null,
                    columnConfig,
                    columnDef: args.columnDefs.find(columnDef => columnDef.key === columnConfig.key)
                }
            );
        }
    }

    public finalizeCellsRendering(): void {
        if (!this.isColumnsInitialized) {
            this.pointerEventsSubscription = this.listenToPointerEvents()
                .subscribe();

            this.isColumnsInitialized = true;
        }
    }

    public columnCellRendered(args: TablePluginColumnCellRenderedArgs): void {
        if (!this.isColumnsInitialized && args.rowIndex === 0) {
            const columnMeta = this.columns[args.columnConfigIndex];

            columnMeta.columnDirective = args.columnDirective;
            columnMeta.viewRef = args.viewRef;
            columnMeta.element = args.columnDirective.getElement(args.viewRef);
        }
    }

    private listenToPointerEvents(): Observable<any> {
        const columnListeners = this.columns.map(columnMeta => this.listenToColumnPointerEvents(columnMeta));

        return NEVER.pipe(
            dependOn(...columnListeners)
        );
    }

    private listenToColumnPointerEvents(columnMeta: TableColumnReorderColumnMeta): Observable<any> {
        return this.dragHandler.init({
            element:                columnMeta.element,
            axis:                   new Set<DragHandlerAxis>([X]),
            movingInitialThreshold: COLUMN_MOVING_INITIAL_THRESHOLD_PX,
            destroy$:               this.destroy$,
            startMoving:            posInfo => this.startColumnMoving(columnMeta, posInfo),
            endMoving:              () => this.finalizeColumnMoving(),
            apply:                  () => this.applyColumnMoving(),
            preview:                posInfo => this.visualizeColumnMoving(posInfo),
            cancel:                 () => null,
            transformPos:           posInfo => posInfo
        });
    }

    private createGhostElement(
        movingArea: TableColumnReorderColumnsArea,
        initialShift: number
    ): DynamicComponentRef<TableColumnMovingGhostComponent> {
        const ghostComponentRef = this.dynamicComponentFactoryService.create(TableColumnMovingGhostComponent, {
            configure: {
                movingArea,
                initialShift
            },
            place: {viewContainerRef: this.afterTableViewContainerRef},
            destroyWhen: this.destroy$,
            injector: this.injector
        });

        // Trigger the change detection in order to be sure that the component's lifecycle hooks will be executed.
        // Otherwise it can be missed in case of OnPush strategy in any parent.
        ghostComponentRef.componentRef.changeDetectorRef.detectChanges();

        return ghostComponentRef;
    }

    private startColumnMoving(columnMeta: TableColumnReorderColumnMeta, posInfo: PointerPositionInfo): void {
        this.message$.next({type: ResizingTablePluginMessageType.TOGGLE_RESIZING});

        const shiftX = posInfo.shift[X];
        const columnsAreas = TableColumnReorderColumnsArea.getAreasByColumns(this.columns);
        const ghostColumnsArea = columnsAreas.find(columnArea => columnArea.columns.includes(columnMeta));
        const ghostComponentRef = this.createGhostElement(ghostColumnsArea, shiftX);

        const initialColumnsAreaIndex = columnsAreas.indexOf(ghostColumnsArea);

        this.columnMovingGhostMeta = {
            shiftX,
            lastPreviewShiftX:                0,
            width:                            ghostColumnsArea.rect.width,
            initialColumnsAreaProjectionLeft: ghostColumnsArea.rect.left,
            columnsAreaProjectionLeft:        ghostColumnsArea.rect.left + shiftX,
            ghostColumnsArea,
            columnsAreas,
            initialColumnsAreaIndex,
            newColumnsAreaIndex:              initialColumnsAreaIndex,
            lastPreviewColumnsAreaIndex:      initialColumnsAreaIndex,
            lastColumnsConfigPreview:         null,
            overlappedColumnsAreas:           [],
            lastAreaToSwapWith:               null,
            isLastMoveDirectionLeft:          null,
            ghostComponentRef
        };

        this.highlightOverlappedColumns();
        this.updateDestination();
    }

    private finalizeColumnMoving(): void {
        const ghostMeta = this.columnMovingGhostMeta;

        if (ghostMeta) {
            ghostMeta.ghostComponentRef.destroy();

            this.toggleClassOnColumnsAreas(
                ghostMeta.overlappedColumnsAreas,
                TABLE_COLUMN_GHOST_OVERLAPPING_CLASS,
                false
            );

            this.columnMovingGhostMeta = null;

            this.message$.next({
                type: CommonTablePluginMessageType.SET_COLUMNS_CONFIG_PREVIEW,
                columnsConfig: null
            } as TablePluginMsgColumnsConfigPreview);
            this.message$.next({type: ResizingTablePluginMessageType.TOGGLE_RESIZING});
        }
    }

    private updateColumnMovingGhostPos(shiftX: number): void {
        const ghostMeta = this.columnMovingGhostMeta;

        ghostMeta.shiftX = shiftX;
        ghostMeta.columnsAreaProjectionLeft = ghostMeta.initialColumnsAreaProjectionLeft + shiftX;
        ghostMeta.ghostComponentRef.instance.updateShift(shiftX);
    }

    private highlightOverlappedColumns(): void {
        const ghostMeta = this.columnMovingGhostMeta;
        const ghostLeft = ghostMeta.columnsAreaProjectionLeft;
        const ghostRight = ghostLeft + ghostMeta.width - 1;
        const overlappedColumnAreas = ghostMeta.columnsAreas
            .filter(columnArea =>
                columnArea !== ghostMeta.ghostColumnsArea
                && columnArea.rect.left < ghostRight
                && columnArea.rect.right > ghostLeft
            );
        const added = _.difference(overlappedColumnAreas, ghostMeta.overlappedColumnsAreas);
        const removed = _.difference(ghostMeta.overlappedColumnsAreas, overlappedColumnAreas);

        this.toggleClassOnColumnsAreas(added, TABLE_COLUMN_GHOST_OVERLAPPING_CLASS, true);
        this.toggleClassOnColumnsAreas(removed, TABLE_COLUMN_GHOST_OVERLAPPING_CLASS, false);

        ghostMeta.overlappedColumnsAreas = overlappedColumnAreas;
    }

    private toggleClassOnColumnsAreas(
        areas: TableColumnReorderColumnsArea[],
        className: string,
        state: boolean
    ): void {
        const classAction = state
            ? (element: HTMLElement) => element.classList.add(className)
            : (element: HTMLElement) => element.classList.remove(className);

        areas.forEach(columnsArea =>
            columnsArea.columns.forEach(columnMeta => classAction(columnMeta.element))
        );
    }

    private updateDestination(): void {
        const ghostMeta = this.columnMovingGhostMeta;
        const isLeftMoving: boolean = ghostMeta.shiftX < ghostMeta.lastPreviewShiftX;
        const ghostLeft = ghostMeta.columnsAreaProjectionLeft;
        const ghostRight = ghostLeft + ghostMeta.width - 1;

        const destinationColumnArea: TableColumnReorderColumnsArea = isLeftMoving
            ? ghostMeta.columnsAreas.find(columnArea =>
                (columnArea.rect.right - ghostLeft) / columnArea.rect.width >= COLUMN_OVERLAPPING_THRESHOLD_FRACTION
            )
            : _.findLast(ghostMeta.columnsAreas, columnArea =>
                (ghostRight - columnArea.rect.left) / columnArea.rect.width >= COLUMN_OVERLAPPING_THRESHOLD_FRACTION
            );

        const destinationColumnsAreaIndex: number = ghostMeta.columnsAreas.indexOf(destinationColumnArea);
        const isOwnArea: boolean = ghostMeta.ghostColumnsArea === destinationColumnArea;
        const isSameMovingDirection: boolean = ghostMeta.isLastMoveDirectionLeft === isLeftMoving;
        const isSameColumnToSwapWith: boolean = ghostMeta.lastAreaToSwapWith === destinationColumnArea;

        if (!isOwnArea && (!isSameMovingDirection || !isSameColumnToSwapWith)) {
            ghostMeta.newColumnsAreaIndex     = destinationColumnsAreaIndex;
            ghostMeta.isLastMoveDirectionLeft = isLeftMoving;
            ghostMeta.lastAreaToSwapWith      = destinationColumnArea;
        }
    }

    private visualizeColumnMoving(posInfo: PointerPositionInfo): void {
        this.updateColumnMovingGhostPos(posInfo.shift[X]);
        this.highlightOverlappedColumns();
        this.updateDestination();
        this.updateColumnMovingPreview();
    }

    private updateColumnMovingPreview(): void {
        const ghostMeta = this.columnMovingGhostMeta;
        const {newColumnsAreaIndex, initialColumnsAreaIndex, lastPreviewColumnsAreaIndex} = ghostMeta;

        if (newColumnsAreaIndex !== lastPreviewColumnsAreaIndex) {
            const isEqualToInitial = newColumnsAreaIndex === initialColumnsAreaIndex;

            // Create new columns list.

            TableColumnReorderPlugin.moveElementInArrayByIndex(
                ghostMeta.columnsAreas,
                lastPreviewColumnsAreaIndex,
                newColumnsAreaIndex
            );

            ghostMeta.lastPreviewColumnsAreaIndex = newColumnsAreaIndex;
            ghostMeta.lastColumnsConfigPreview = isEqualToInitial
                ? null
                : TableColumnReorderColumnsArea.mapAreasToConfigs(
                    ghostMeta.columnsAreas
                );

            // Render the preview.

            this.message$.next({
                type: CommonTablePluginMessageType.SET_COLUMNS_CONFIG_PREVIEW,
                columnsConfig: ghostMeta.lastColumnsConfigPreview
            } as TablePluginMsgColumnsConfigPreview);

            // Update column areas.

            ghostMeta.columnsAreas.forEach(columnArea => columnArea.updateRect());
            ghostMeta.lastPreviewShiftX = ghostMeta.shiftX;

            this.highlightOverlappedColumns();
        }
    }

    private applyColumnMoving(): void {
        const ghostMeta = this.columnMovingGhostMeta;
        const {initialColumnsAreaIndex, lastPreviewColumnsAreaIndex} = ghostMeta;
        const isEqualToInitial = lastPreviewColumnsAreaIndex === initialColumnsAreaIndex;

        if (!isEqualToInitial) {

            // Apply changes to the internal column array.
            const newColumns = TableColumnReorderColumnsArea.mapAreasToColumnsMeta(ghostMeta.columnsAreas);

            this.columns.splice(0, this.columns.length, ...newColumns);

            this.message$.next({
                type: CommonTablePluginMessageType.SET_COLUMNS_CONFIG,
                columnsConfig: newColumns.map(columnMeta => columnMeta.columnConfig)
            } as TablePluginMsgColumnsConfig);
        }
    }

}
